/**
 * SpectatorWorkspace — Public, mobile-first, read-only workspace view.
 *
 * URL: /live/:shareId
 *
 * Features:
 * - Auto-connects to WebSocket via spectator share_id
 * - Multi-project support: dropdown to switch between session projects
 * - Follow presenter mode: auto-follows presenter's active project
 * - Explore mode: spectator picks a project, still sees live mutations for it
 * - Auto-refreshes graph on mutating tool calls
 * - Mobile-first: graph takes ~85% of screen, feed is collapsible bottom sheet
 * - No authentication required
 */
import { useEffect, useRef, useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSpectatorSocket } from '@/hooks/workspace/useSpectatorSocket'
import { useSpectatorProjectData } from '@/hooks/workspace/useSpectatorProjectData'
import { ProjectGraph } from '@/components/workspace/ProjectGraph'
import { NodeDetailPopover } from '@/components/workspace/NodeDetailPopover'
import { FloatingReactions } from '@/components/workspace/FloatingReactions'
import {
  Wifi,
  WifiOff,
  Loader2,
  ChevronUp,
  ChevronDown,
  Clock,
  Maximize,
  Minimize,
  Eye,
  Radio,
  ChevronRight,
  Lock,
  Globe,
  LockKeyhole,
  Copy,
  Check,
  X,
} from 'lucide-react'
import type { ToolCallEvent } from '@/hooks/workspace/useWorkspaceSocket'
import { getToolLabel } from '@/lib/tool-labels'
import { authUtils } from '@/lib/api/auth'
import { refreshAccessToken } from '@/lib/api/refresh'
import axios from 'axios'

/** Minimal project info tracked from WebSocket events */
interface SessionProject {
  id: string
  name: string
  createdAt: string
}

export default function SpectatorWorkspace() {
  const { shareId } = useParams<{ shareId: string }>()
  const safeShareId = shareId || ''

  // Validate link before connecting WebSocket
  const [linkStatus, setLinkStatus] = useState<
    | 'checking'
    | 'valid'
    | 'expired' // 410 — server says session expired, or presenter revoked
    | 'invalid' // missing share id
    | 'not_found' // 404 — share id never existed
    | 'forbidden' // 401/403 — link revoked or auth not accepted
    | 'rate_limited' // 429
    | 'server_error' // 5xx
    | 'network_error' // request never reached server
    | 'needs_login'
  >('checking')
  const [authToken, setAuthToken] = useState<string | null>(null)
  const [initialProjectId, setInitialProjectId] = useState<string | null>(null)
  const [isOwner, setIsOwner] = useState(false)
  const [linkType, setLinkType] = useState<'project' | 'session'>('session')
  const [isPublic, setIsPublic] = useState(false)
  const [togglingVisibility, setTogglingVisibility] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!safeShareId) {
      setLinkStatus('invalid')
      return
    }

    // Spectator view always lives on the same origin as its API by design,
    // so use a relative path unconditionally. (The previous host-conditional
    // pointed dev hosts at the prod API and broke spectator on dev — see
    // bd ecosemantic-qxj.)
    const base = '/api/v1'

    const existingToken = authUtils.getToken()
    const headers: Record<string, string> = {}
    if (existingToken) headers['Authorization'] = `Bearer ${existingToken}`

    axios
      .get(`${base}/workspace/spectator/${safeShareId}/validate`, { headers })
      .then((resp) => {
        const requiresAuth = resp.data.requires_auth === true
        if (requiresAuth) {
          const token = authUtils.getToken()
          if (!token) {
            setLinkStatus('needs_login')
            return
          }
          setAuthToken(token)
        }
        // Per-project links: always use the link's project_id (that's the
        // whole point — share THIS project). Session-wide links: prefer the
        // presenter's most recent project from tool_usage_log so spectators
        // rehydrate on reload.
        const isProjectLink = !!resp.data.project_id
        const rehydratedProjectId = isProjectLink
          ? resp.data.project_id
          : resp.data.presenter_active_project_id
        if (rehydratedProjectId) {
          setInitialProjectId(rehydratedProjectId)
        }
        if (resp.data.is_owner) setIsOwner(true)
        // Per-project links have a project_id; session-wide links don't
        if (resp.data.project_id) {
          setLinkType('project')
          setIsPublic(resp.data.is_public === true)
        }
        setLinkStatus('valid')
      })
      .catch((err: unknown) => {
        // Status-discriminated handling (no silent fallback): map each error
        // class to a distinct UI state so the user sees a truthful message
        // rather than a generic "Session Ended."
        if (!axios.isAxiosError(err)) {
          console.error('[Spectator] validate failed (non-axios error):', err)
          setLinkStatus('server_error')
          return
        }
        if (!err.response) {
          // Request fired but never got a response — offline, DNS, CORS, etc.
          setLinkStatus('network_error')
          return
        }
        const status = err.response.status
        if (status === 401 || status === 403) {
          setLinkStatus('forbidden')
        } else if (status === 404) {
          setLinkStatus('not_found')
        } else if (status === 410) {
          setLinkStatus('expired')
        } else if (status === 429) {
          setLinkStatus('rate_limited')
        } else if (status >= 500) {
          setLinkStatus('server_error')
        } else {
          // Anything else (e.g. 400) — surface as server error rather than
          // pretending the link expired.
          console.error('[Spectator] validate returned unexpected status:', status)
          setLinkStatus('server_error')
        }
      })
  }, [safeShareId])

  // Presenter controls: revoke link and copy link
  const revokeLink = useCallback(async () => {
    if (revoking) return
    setRevoking(true)
    try {
      const token = authUtils.getToken()
      // Same-origin: spectator API is always served from the same origin
      // as the spectator view (see validate effect above for context).
      const base = '/api/v1'
      await axios.delete(`${base}/workspace/spectator/${safeShareId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setLinkStatus('expired')
    } catch (err) {
      console.error('Failed to revoke link:', err)
    } finally {
      setRevoking(false)
    }
  }, [safeShareId, revoking])

  // Per-project links: toggle public/private
  const toggleVisibility = useCallback(async () => {
    if (togglingVisibility) return
    setTogglingVisibility(true)
    try {
      const token = authUtils.getToken()
      // Same-origin (see validate effect for context).
      const base = '/api/v1'
      const resp = await axios.patch(
        `${base}/workspace/spectator/${safeShareId}/visibility`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      )
      setIsPublic(resp.data.is_public)
    } catch (err) {
      console.error('Failed to toggle visibility:', err)
    } finally {
      setTogglingVisibility(false)
    }
  }, [safeShareId, togglingVisibility])

  const copyLink = useCallback(() => {
    const url = `https://ecosemantic.com/live/${safeShareId}`
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => {})
  }, [safeShareId])

  // Token refresh for private links — called by socket hook on 4003
  const refreshAuthToken = useCallback(async (): Promise<string | null> => {
    console.log('[Spectator] Refreshing access token...')
    const newToken = await refreshAccessToken()
    if (newToken) {
      setAuthToken(newToken)
      console.log('[Spectator] Token refreshed successfully')
      return newToken
    }
    setLinkStatus('needs_login')
    return null
  }, [])

  const {
    events,
    connected,
    connecting,
    error: wsError,
    includeExisting,
    linkCreatedAt,
    spectatorCount,
    reactions,
    revoked,
    sendReaction,
    connect,
  } = useSpectatorSocket(safeShareId, authToken, refreshAuthToken)

  // Presenter revoked the session — show expired view
  useEffect(() => {
    if (revoked) setLinkStatus('expired')
  }, [revoked])

  const {
    project,
    activities,
    loading: projectLoading,
    loadProject,
    refreshProject,
  } = useSpectatorProjectData(safeShareId, authToken)

  const [feedOpen, setFeedOpen] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [selectedDetail, setSelectedDetail] = useState<{
    nodeId: string
    nodeType: 'activity' | 'exchange'
  } | null>(null)
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false)

  // Multi-project state
  const [sessionProjects, setSessionProjects] = useState<SessionProject[]>([])
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null)
  const [followPresenter, setFollowPresenter] = useState(true)
  const [presenterProjectId, setPresenterProjectId] = useState<string | null>(null)

  const currentProjectIdRef = useRef<string | null>(null)
  const processedCallIdsRef = useRef<Set<string>>(new Set())
  const initializedRef = useRef(false)
  const mutationCountRef = useRef(0)
  const lastFetchedAtRef = useRef(0)
  const refreshingRef = useRef(false)
  const refreshProjectRef = useRef(refreshProject)
  refreshProjectRef.current = refreshProject
  const followPresenterRef = useRef(followPresenter)
  followPresenterRef.current = followPresenter

  const markDirty = useCallback(() => {
    mutationCountRef.current += 1
  }, [])

  const handleNodeClick = useCallback((nodeId: string, nodeType: 'activity' | 'exchange') => {
    setSelectedDetail((prev) => (prev?.nodeId === nodeId ? null : { nodeId, nodeType }))
  }, [])

  // Switch to a specific project (used by dropdown)
  const switchToProject = useCallback(
    (projectId: string) => {
      if (projectId === currentProjectIdRef.current) return
      setCurrentProjectId(projectId)
      currentProjectIdRef.current = projectId
      loadProject(projectId)
      setSelectedDetail(null)
      // Reset mutation tracking for the new project
      mutationCountRef.current = 0
      lastFetchedAtRef.current = 0
    },
    [loadProject],
  )

  // Return to follow-presenter mode
  const enableFollowPresenter = useCallback(() => {
    setFollowPresenter(true)
    setProjectDropdownOpen(false)
    // If presenter is on a different project, switch to it
    if (presenterProjectId && presenterProjectId !== currentProjectIdRef.current) {
      switchToProject(presenterProjectId)
    }
  }, [presenterProjectId, switchToProject])

  // Spectator picks a project — enter explore mode
  const selectProject = useCallback(
    (projectId: string) => {
      setFollowPresenter(false)
      setProjectDropdownOpen(false)
      switchToProject(projectId)
    },
    [switchToProject],
  )

  // Refresh poll loop
  useEffect(() => {
    const interval = setInterval(async () => {
      if (refreshingRef.current || !currentProjectIdRef.current) return
      if (mutationCountRef.current === lastFetchedAtRef.current) return
      refreshingRef.current = true
      try {
        while (mutationCountRef.current !== lastFetchedAtRef.current) {
          lastFetchedAtRef.current = mutationCountRef.current
          await refreshProjectRef.current()
        }
      } finally {
        refreshingRef.current = false
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [])

  // Auto-connect WebSocket only after validation
  useEffect(() => {
    if (safeShareId && linkStatus === 'valid') connect()
  }, [safeShareId, linkStatus, connect])

  // Auto-load project from persistent live link (project_id from validate)
  useEffect(() => {
    if (initialProjectId && connected && !currentProjectId) {
      setSessionProjects((prev) => {
        if (prev.some((p) => p.id === initialProjectId)) return prev
        return [
          ...prev,
          { id: initialProjectId, name: 'Loading...', createdAt: new Date().toISOString() },
        ]
      })
      switchToProject(initialProjectId)
    }
  }, [initialProjectId, connected, currentProjectId, switchToProject])

  // Sync the loaded project's real name back into sessionProjects so the
  // dropdown shows the actual name instead of the 'Loading...' placeholder
  // that was seeded before project data was fetched.
  useEffect(() => {
    if (!project?.id || !project?.project_name) return
    setSessionProjects((prev) => {
      const existing = prev.find((p) => p.id === project.id)
      if (!existing) return prev
      if (existing.name === project.project_name) return prev
      return prev.map((p) => (p.id === project.id ? { ...p, name: project.project_name } : p))
    })
  }, [project?.id, project?.project_name])

  // Tools that target a specific project (have project_id in params)
  const PROJECT_TOOLS = new Set([
    'create_activity',
    'update_activity',
    'delete_activity',
    'update_project',
    'create_custom_exchange',
    'create_database_exchange',
    'update_exchange',
    'delete_exchange',
  ])
  // Tools that mutate an activity's exchanges (no project_id, but affect current project context)
  const MUTATION_TOOLS = new Set([
    'create_activity',
    'update_activity',
    'delete_activity',
    'create_custom_exchange',
    'create_database_exchange',
    'update_exchange',
    'delete_exchange',
    'update_project',
  ])

  // Helper: extract project id from a create_project event
  const extractProjectFromCreate = (ev: ToolCallEvent): { id: string; name: string } | null => {
    const preview = ev.data.result_preview as Record<string, unknown> | undefined
    const id = preview?.id as string | undefined
    const name = (preview?.project_name || ev.data.params?.name) as string | undefined
    if (id) return { id, name: name || 'Unnamed project' }
    return null
  }

  // Helper: extract project_id from mutation events
  const extractProjectIdFromMutation = (ev: ToolCallEvent): string | null => {
    // Prefer the first-class event field (new backend); fall back to params
    // only for in-flight events from an older backend during rollout.
    const topLevel = (ev.data as { project_id?: string }).project_id
    if (topLevel) return topLevel
    return (ev.data.params?.project_id as string) || null
  }

  const shouldAcceptProject = (ev: ToolCallEvent): boolean => {
    if (includeExisting) return true
    if (!linkCreatedAt) return true
    return ev.timestamp >= linkCreatedAt
  }

  // Main event processing
  useEffect(() => {
    if (events.length === 0) return

    // Per-project links: skip project discovery and switching — only refresh
    // on mutations targeting the locked project.
    if (linkType === 'project') {
      for (const ev of events) {
        if (ev.data.status !== 'completed' || !ev.call_id) continue
        if (processedCallIdsRef.current.has(ev.call_id)) continue
        processedCallIdsRef.current.add(ev.call_id)

        if (MUTATION_TOOLS.has(ev.data.tool_name)) {
          const mutationPid = extractProjectIdFromMutation(ev)
          if (!mutationPid || mutationPid === currentProjectIdRef.current) {
            markDirty()
          }
        }
      }
      return
    }

    // Session-wide links: full multi-project event processing
    // Seed on first run
    if (!initializedRef.current) {
      initializedRef.current = true
      const projects: SessionProject[] = []
      let latestPresenterProject: string | null = null

      for (const ev of events) {
        if (ev.call_id && ev.data.status === 'completed') {
          processedCallIdsRef.current.add(ev.call_id)

          if (ev.data.tool_name === 'create_project' && shouldAcceptProject(ev)) {
            const p = extractProjectFromCreate(ev)
            if (p) {
              projects.push({ id: p.id, name: p.name, createdAt: ev.timestamp })
              latestPresenterProject = p.id
            }
          }
          // Track presenter's active project from mutations with project_id
          if (PROJECT_TOOLS.has(ev.data.tool_name)) {
            const pid = extractProjectIdFromMutation(ev)
            if (pid) latestPresenterProject = pid
          }
        }
      }

      if (projects.length > 0) {
        setSessionProjects((prev) => {
          const merged = new Map(prev.map((p) => [p.id, p]))
          for (const p of projects) merged.set(p.id, p)
          return Array.from(merged.values())
        })
      }
      if (latestPresenterProject) {
        setPresenterProjectId(latestPresenterProject)
        if (followPresenterRef.current) {
          switchToProject(latestPresenterProject)
        }
      } else if (currentProjectIdRef.current) {
        markDirty()
      }
      return
    }

    // Process new completed events
    let needsRefresh = false

    for (const ev of events) {
      if (ev.data.status !== 'completed' || !ev.call_id) continue
      if (processedCallIdsRef.current.has(ev.call_id)) continue
      processedCallIdsRef.current.add(ev.call_id)

      // New project created
      if (ev.data.tool_name === 'create_project' && shouldAcceptProject(ev)) {
        const p = extractProjectFromCreate(ev)
        if (p) {
          setSessionProjects((prev) => {
            if (prev.some((sp) => sp.id === p.id)) return prev
            return [...prev, { id: p.id, name: p.name, createdAt: ev.timestamp }]
          })
          setPresenterProjectId(p.id)
          if (followPresenterRef.current) {
            switchToProject(p.id)
          }
        }
        continue
      }

      // Track presenter's active project from mutations
      if (PROJECT_TOOLS.has(ev.data.tool_name)) {
        const pid = extractProjectIdFromMutation(ev)
        if (pid) {
          setPresenterProjectId(pid)
          if (followPresenterRef.current && pid !== currentProjectIdRef.current) {
            switchToProject(pid)
          }
        }
      }

      // Refresh graph if mutation targets current project
      if (MUTATION_TOOLS.has(ev.data.tool_name)) {
        const mutationPid = extractProjectIdFromMutation(ev)
        // If mutation has project_id, only refresh if it matches current project
        // If no project_id (e.g. exchange tools), assume it targets current presenter project
        if (!mutationPid || mutationPid === currentProjectIdRef.current) {
          needsRefresh = true
        }
      }
    }

    if (processedCallIdsRef.current.size > 200) {
      const arr = [...processedCallIdsRef.current]
      processedCallIdsRef.current = new Set(arr.slice(-100))
    }

    if (needsRefresh && currentProjectIdRef.current) {
      markDirty()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, markDirty, switchToProject, includeExisting, linkCreatedAt, linkType])

  // Recent events for the feed (last 10)
  const recentEvents = events.slice(0, 10)

  // Thinking indicator — only show if a tool call has been pending for >500ms
  const [visiblePending, setVisiblePending] = useState<ToolCallEvent | null>(null)
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const pending = events.find(
      (ev) =>
        ev.data.status === 'started' &&
        ev.call_id &&
        !events.some((other) => other.call_id === ev.call_id && other.data.status !== 'started'),
    )
    if (!pending) {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current)
        pendingTimerRef.current = null
      }
      setVisiblePending(null)
      return
    }
    if (visiblePending?.call_id !== pending.call_id) {
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current)
      pendingTimerRef.current = setTimeout(() => {
        setVisiblePending(pending)
        pendingTimerRef.current = null
      }, 500)
    }
    return () => {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current)
        pendingTimerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events])

  const statusColor = connected ? 'text-green-400' : connecting ? 'text-yellow-400' : 'text-red-400'
  const StatusIcon = connected ? Wifi : connecting ? Loader2 : WifiOff

  // Early returns for invalid/loading/expired states
  if (!safeShareId) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-950 text-white">
        <p className="text-lg text-slate-400">Invalid spectator link</p>
      </div>
    )
  }

  if (linkStatus === 'checking') {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-950 text-white gap-5 px-6">
        <img src="/logo-square.svg" alt="EcoSemantic" className="w-14 h-14" />
        <div className="text-center">
          <h1 className="text-lg font-semibold text-slate-200 mb-1">Live LCA Session</h1>
          <p className="text-sm text-slate-500 max-w-xs">
            You're about to watch a live Life Cycle Assessment session on EcoSemantic.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
          <span className="text-sm text-slate-400">Connecting…</span>
        </div>
      </div>
    )
  }

  if (linkStatus === 'expired') {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-950 text-white gap-4 px-6">
        <img src="/logo-square.svg" alt="EcoSemantic" className="w-12 h-12 opacity-50" />
        <Clock className="w-10 h-10 text-slate-600" />
        <div className="text-center">
          <h1 className="text-xl font-semibold text-slate-200 mb-2">Session Ended</h1>
          <p className="text-sm text-slate-500 max-w-xs">
            This live session has expired or been revoked by the presenter.
          </p>
        </div>
        <a
          href="https://ecosemantic.com"
          className="mt-4 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
        >
          Visit EcoSemantic →
        </a>
      </div>
    )
  }

  if (linkStatus === 'not_found') {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-950 text-white gap-4 px-6">
        <img src="/logo-square.svg" alt="EcoSemantic" className="w-12 h-12 opacity-50" />
        <X className="w-10 h-10 text-slate-600" />
        <div className="text-center">
          <h1 className="text-xl font-semibold text-slate-200 mb-2">Session Not Found</h1>
          <p className="text-sm text-slate-500 max-w-xs">
            This live link doesn't exist. Double-check the URL, or ask the presenter for a new link.
          </p>
        </div>
        <a
          href="https://ecosemantic.com"
          className="mt-4 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
        >
          Visit EcoSemantic →
        </a>
      </div>
    )
  }

  if (linkStatus === 'forbidden') {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-950 text-white gap-4 px-6">
        <img src="/logo-square.svg" alt="EcoSemantic" className="w-12 h-12 opacity-50" />
        <Lock className="w-10 h-10 text-slate-600" />
        <div className="text-center">
          <h1 className="text-xl font-semibold text-slate-200 mb-2">Access Denied</h1>
          <p className="text-sm text-slate-500 max-w-xs">
            This link has been revoked, or your account isn't authorized to view it.
          </p>
        </div>
        <a
          href="https://ecosemantic.com"
          className="mt-4 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
        >
          Visit EcoSemantic →
        </a>
      </div>
    )
  }

  if (linkStatus === 'rate_limited') {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-950 text-white gap-4 px-6">
        <img src="/logo-square.svg" alt="EcoSemantic" className="w-12 h-12 opacity-50" />
        <Clock className="w-10 h-10 text-slate-600" />
        <div className="text-center">
          <h1 className="text-xl font-semibold text-slate-200 mb-2">Too Many Requests</h1>
          <p className="text-sm text-slate-500 max-w-xs">
            We're rate-limiting this link right now. Please wait a moment and reload the page.
          </p>
        </div>
      </div>
    )
  }

  if (linkStatus === 'server_error') {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-950 text-white gap-4 px-6">
        <img src="/logo-square.svg" alt="EcoSemantic" className="w-12 h-12 opacity-50" />
        <WifiOff className="w-10 h-10 text-slate-600" />
        <div className="text-center">
          <h1 className="text-xl font-semibold text-slate-200 mb-2">Server Error</h1>
          <p className="text-sm text-slate-500 max-w-xs">
            Something went wrong on our end while loading this session. Please try reloading in a
            moment.
          </p>
        </div>
      </div>
    )
  }

  if (linkStatus === 'network_error') {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-950 text-white gap-4 px-6">
        <img src="/logo-square.svg" alt="EcoSemantic" className="w-12 h-12 opacity-50" />
        <WifiOff className="w-10 h-10 text-slate-600" />
        <div className="text-center">
          <h1 className="text-xl font-semibold text-slate-200 mb-2">Connection Problem</h1>
          <p className="text-sm text-slate-500 max-w-xs">
            We couldn't reach the EcoSemantic server. Check your network connection and try again.
          </p>
        </div>
      </div>
    )
  }

  if (linkStatus === 'needs_login') {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-950 text-white gap-4 px-6">
        <img src="/logo-square.svg" alt="EcoSemantic" className="w-12 h-12" />
        <Lock className="w-10 h-10 text-slate-500" />
        <div className="text-center">
          <h1 className="text-xl font-semibold text-slate-200 mb-2">Private Session</h1>
          <p className="text-sm text-slate-500 max-w-xs">
            This live view requires authentication. Please log in to access it.
          </p>
        </div>
        <a
          href={`/auth?redirect=${encodeURIComponent(window.location.pathname)}`}
          className="mt-4 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
        >
          Log in →
        </a>
      </div>
    )
  }

  // Current project name for display
  const currentProjectName =
    project?.project_name || sessionProjects.find((p) => p.id === currentProjectId)?.name || null

  return (
    <div className="h-[100dvh] flex flex-col bg-slate-950 overflow-hidden">
      {/* Presenter toolbar — only visible to link owner */}
      {isOwner && !fullscreen && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800/90 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            {linkType === 'session' ? (
              <>
                <Radio className="w-3.5 h-3.5 text-red-400" />
                <span className="text-xs text-slate-300">You are the presenter</span>
              </>
            ) : (
              <>
                {isPublic ? (
                  <Globe className="w-3.5 h-3.5 text-green-400" />
                ) : (
                  <LockKeyhole className="w-3.5 h-3.5 text-amber-400" />
                )}
                <span className="text-xs text-slate-300">
                  {isPublic ? 'Public — anyone with link can view' : 'Private — only you can view'}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {linkType === 'session' ? (
              <button
                onClick={revokeLink}
                disabled={revoking}
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded bg-red-600/20 text-red-300 hover:bg-red-600/30 transition-colors disabled:opacity-50"
              >
                <X className="w-3 h-3" />
                {revoking ? 'Revoking...' : 'Revoke Link'}
              </button>
            ) : (
              <button
                onClick={toggleVisibility}
                disabled={togglingVisibility}
                className={`text-xs px-2.5 py-1 rounded transition-colors ${
                  isPublic
                    ? 'bg-amber-600/20 text-amber-300 hover:bg-amber-600/30'
                    : 'bg-green-600/20 text-green-300 hover:bg-green-600/30'
                } disabled:opacity-50`}
              >
                {togglingVisibility ? '...' : isPublic ? 'Make Private' : 'Make Public'}
              </button>
            )}
            <button
              onClick={copyLink}
              className="flex items-center gap-1 text-xs px-2.5 py-1 rounded bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied' : 'Copy Link'}
            </button>
          </div>
        </div>
      )}

      {/* Compact header — hidden in fullscreen */}
      {!fullscreen && (
        <div className="flex items-center justify-between px-3 py-2 bg-slate-900/80 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <img src="/logo-square.svg" alt="EcoSemantic" className="w-6 h-6" />
            <span className="text-sm font-semibold text-white">EcoSemantic</span>
            <span className="text-xs text-slate-500">LIVE</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Project switcher dropdown — session-wide links only */}
            {linkType === 'session' && sessionProjects.length > 1 ? (
              <div className="relative">
                <button
                  onClick={() => setProjectDropdownOpen((prev) => !prev)}
                  className="flex items-center gap-1 text-xs text-slate-300 bg-slate-800 hover:bg-slate-700 rounded px-2 py-1 max-w-[160px] transition-colors"
                >
                  <span className="truncate">{currentProjectName || 'No project'}</span>
                  <ChevronDown className="w-3 h-3 flex-shrink-0 text-slate-500" />
                </button>
                {projectDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-30"
                      onClick={() => setProjectDropdownOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-1 z-40 bg-slate-800 border border-slate-700 rounded-lg shadow-xl min-w-[200px] max-w-[280px] py-1 overflow-hidden">
                      {/* Follow presenter option */}
                      {!followPresenter && (
                        <button
                          onClick={enableFollowPresenter}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-indigo-400 hover:bg-slate-700/50 transition-colors border-b border-slate-700"
                        >
                          <Radio className="w-3.5 h-3.5" />
                          <span className="font-medium">Follow presenter</span>
                        </button>
                      )}
                      {/* Project list */}
                      {sessionProjects.map((sp) => {
                        const isActive = sp.id === currentProjectId
                        const isPresenterActive = sp.id === presenterProjectId
                        return (
                          <button
                            key={sp.id}
                            onClick={() => selectProject(sp.id)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                              isActive
                                ? 'bg-slate-700/70 text-white'
                                : 'text-slate-300 hover:bg-slate-700/50'
                            }`}
                          >
                            <span className="truncate flex-1 text-left">{sp.name}</span>
                            {isPresenterActive && (
                              <span
                                className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-green-400"
                                title="Presenter is here"
                              />
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </>
                )}
              </div>
            ) : currentProjectName ? (
              <span className="text-xs text-slate-400 truncate max-w-[150px]">
                {currentProjectName}
              </span>
            ) : null}

            {/* Follow presenter indicator */}
            {linkType === 'session' && followPresenter && sessionProjects.length > 1 && (
              <span className="flex items-center gap-1 text-[10px] text-indigo-400">
                <Radio className="w-3 h-3" />
              </span>
            )}

            {connected && spectatorCount > 0 && (
              <span className="flex items-center gap-1 text-xs text-slate-400 bg-slate-800 rounded-full px-2 py-0.5">
                <Eye className="w-3 h-3" />
                {spectatorCount}
              </span>
            )}
            <StatusIcon className={`w-4 h-4 ${statusColor} ${connecting ? 'animate-spin' : ''}`} />
            <button
              onClick={() => setFullscreen(true)}
              className="ml-1 p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition-colors"
              title="Fullscreen graph"
            >
              <Maximize className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Follow presenter banner — shown when in explore mode and presenter moves to a different project */}
      {!followPresenter &&
        presenterProjectId &&
        presenterProjectId !== currentProjectId &&
        !fullscreen && (
          <button
            onClick={enableFollowPresenter}
            className="flex items-center justify-center gap-2 px-3 py-1.5 bg-indigo-600/80 hover:bg-indigo-600 text-white text-xs transition-colors"
          >
            <Radio className="w-3.5 h-3.5" />
            <span>Presenter moved to another project</span>
            <ChevronRight className="w-3 h-3" />
            <span className="font-medium">Follow</span>
          </button>
        )}

      {/* Graph area — takes all remaining space */}
      <div className="flex-1 min-h-0 relative">
        {!currentProjectId && !projectLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 z-10">
            <Loader2 className="w-8 h-8 animate-spin mb-3 text-slate-600" />
            <p className="text-sm">Waiting for presenter to start...</p>
            {connected && (
              <p className="text-xs text-slate-600 mt-1">Connected — watching for activity</p>
            )}
          </div>
        )}

        {projectLoading && !activities.length && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
          </div>
        )}

        {activities.length > 0 && (
          <ProjectGraph
            activities={activities}
            selectedNodeId={null}
            selectedNodeType={null}
            onNodeClick={handleNodeClick}
            spectatorMode
          />
        )}

        {wsError && (
          <div className="absolute top-2 left-2 right-2 bg-red-900/80 text-red-200 text-xs px-3 py-1.5 rounded z-20">
            {wsError}
          </div>
        )}

        {/* Thinking indicator */}
        {visiblePending && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-slate-800/90 backdrop-blur-sm border border-slate-700/50 rounded-full px-4 py-1.5 shadow-lg">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500" />
            </span>
            <span className="text-xs text-slate-300">
              {getToolLabel(visiblePending.data.tool_name).icon}{' '}
              {getToolLabel(visiblePending.data.tool_name).label}…
            </span>
          </div>
        )}

        <FloatingReactions reactions={reactions} />

        {/* Reaction buttons */}
        {connected && (
          <div className="absolute bottom-3 left-3 z-20 flex gap-1.5">
            {['👏', '🔥', '🤔', '💚'].map((emoji) => (
              <button
                key={emoji}
                onClick={() => sendReaction(emoji)}
                className="w-9 h-9 rounded-full bg-slate-800/70 hover:bg-slate-700/90 backdrop-blur-sm text-lg flex items-center justify-center transition-transform active:scale-90"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        {/* Exit fullscreen button */}
        {fullscreen && (
          <button
            onClick={() => setFullscreen(false)}
            className="absolute top-3 right-3 z-20 p-1.5 rounded-md bg-slate-800/70 hover:bg-slate-700/80 text-slate-400 hover:text-slate-200 transition-colors backdrop-blur-sm"
            title="Exit fullscreen"
          >
            <Minimize className="w-4 h-4" />
          </button>
        )}

        {/* Node detail popover */}
        {selectedDetail &&
          (() => {
            const act = activities.find((a) => a.id === selectedDetail.nodeId)
            if (act) {
              return (
                <NodeDetailPopover
                  nodeId={selectedDetail.nodeId}
                  nodeType="activity"
                  activity={act}
                  onClose={() => setSelectedDetail(null)}
                />
              )
            }
            // Find exchange and its parent activity
            for (const a of activities) {
              const exc = a.exchanges.find((e) => e.id === selectedDetail.nodeId)
              if (exc) {
                // Detect cross-project type (stored as technosphere but with project: database prefix)
                const effectiveType =
                  exc.exchange_type === 'technosphere' && exc.input_database?.startsWith('project:')
                    ? 'cross-project'
                    : exc.exchange_type
                return (
                  <NodeDetailPopover
                    nodeId={selectedDetail.nodeId}
                    nodeType="exchange"
                    exchange={exc}
                    exchangeType={effectiveType}
                    parentActivity={a}
                    onClose={() => setSelectedDetail(null)}
                  />
                )
              }
            }
            return null
          })()}
      </div>

      {/* Collapsible bottom feed — hidden in fullscreen */}
      {!fullscreen && (
        <div
          className={`bg-slate-900 border-t border-slate-800 transition-all duration-300 ${
            feedOpen ? 'max-h-[40vh]' : 'max-h-10'
          } overflow-hidden`}
        >
          <button
            onClick={() => setFeedOpen(!feedOpen)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs text-slate-400 hover:text-slate-300"
          >
            <span className="font-medium">Activity Feed ({events.length})</span>
            {feedOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>

          {feedOpen && (
            <div className="px-3 pb-2 overflow-y-auto max-h-[calc(40vh-40px)] space-y-1">
              {recentEvents.length === 0 ? (
                <p className="text-xs text-slate-600 py-2">No events yet</p>
              ) : (
                recentEvents.map((ev, i) => <FeedItem key={ev.call_id || i} event={ev} />)
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Format tool params into a readable one-liner for the feed
function formatParamPreview(toolName: string, params?: Record<string, unknown>): string | null {
  if (!params || Object.keys(params).length === 0) return null
  const p = params
  switch (toolName) {
    case 'search_activities':
    case 'search_biosphere':
    case 'search_methods':
      return [p.q, p.location, p.database].filter(Boolean).join(', ')
    case 'read_activity':
      return [p.name || p.code, p.database].filter(Boolean).join(', ')
    case 'calculate_standard':
    case 'calculate_custom':
      return [p.method, p.activity_name || p.code].filter(Boolean).join(' · ')
    case 'create_project':
      return (p.name as string) || null
    case 'create_activity':
      return [p.name, p.location].filter(Boolean).join(', ')
    case 'create_database_exchange':
    case 'create_custom_exchange':
      return [p.input_name || p.input_code, p.amount && `${p.amount} ${p.unit || ''}`]
        .filter(Boolean)
        .join(' · ')
    case 'advanced_search':
      return p.query ? String(p.query).slice(0, 80) : null
    default: {
      const vals = Object.values(p)
        .filter((v) => typeof v === 'string' && v.length > 0 && v.length < 100)
        .slice(0, 2) as string[]
      return vals.length > 0 ? vals.join(', ') : null
    }
  }
}

// Compact feed item for mobile
function FeedItem({ event }: { event: ToolCallEvent }) {
  const statusColors: Record<string, string> = {
    started: 'text-yellow-400',
    completed: 'text-green-400',
    failed: 'text-red-400',
  }
  const { label, icon } = getToolLabel(event.data.tool_name)
  const paramPreview =
    event.data.status === 'completed'
      ? formatParamPreview(event.data.tool_name, event.data.params)
      : null
  const time = new Date(event.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

  return (
    <div className="flex flex-col gap-0.5 text-xs py-1 border-b border-slate-800/50 last:border-0">
      <div className="flex items-center gap-2">
        <span className="text-sm leading-none">{icon}</span>
        <span
          className={`font-medium truncate flex-1 ${statusColors[event.data.status] || 'text-slate-400'}`}
        >
          {label}
        </span>
        {event.data.response_time_ms && (
          <span className="text-slate-600">{event.data.response_time_ms}ms</span>
        )}
        <span className="text-slate-600 tabular-nums">{time}</span>
      </div>
      {paramPreview && (
        <div className="ml-7 text-[11px] text-slate-500 truncate">{paramPreview}</div>
      )}
    </div>
  )
}
